/**
 * Spec 049: a staff member's accounts in apps — what its emulator signs in with. People store
 * them here; the password goes straight to the tasker's secrets store and is shown only to the
 * person who stored it, on «Показать». The «Агенты» app of that person's PC gets it to sign in.
 */

import { api } from "@/api/client";

export type AccountApp =
  | "google" | "instagram" | "tiktok" | "youtube" | "x" | "threads" | "telegram" | "reddit"
  | "linkedin" | "pinterest" | "vk";
export type AccountMark = "ok" | "needs_code" | "maybe_banned" | "banned" | "disabled";
export type AccountState = "free" | "busy" | "needs_code" | "maybe_banned" | "banned" | "disabled";

export interface AccountOut {
  id: string;
  agent_id: string;
  agent_name: string;
  app: AccountApp;
  login: string;
  note: string | null;
  position: number;
  mark: AccountMark;
  mark_note: string | null;
  mark_by: string | null;
  mark_at: string | null;
  state: AccountState;
  held: { launch_id: string; device_id: string | null; device_name: string | null; since: string } | null;
  has_password: boolean;
  /** I stored it: «Показать» and a new password are mine. */
  mine: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
  version: number;
}

export interface AccountDetail extends AccountOut {
  history: { at: string; type: string; by: string | null; payload: Record<string, unknown> }[];
}

export interface AccountCreateIn {
  agent_id: string;
  app: AccountApp;
  login: string;
  password: string;
  note?: string | null;
  position?: number;
}

export interface AccountUpdateIn {
  version: number;
  login?: string;
  note?: string | null;
  position?: number;
  mark?: "ok" | "banned" | "disabled";
  mark_note?: string;
  release?: boolean;
}

/** A PC tool's readiness, as the «Агенты» app reports it (Spec 049/050). */
export interface DeviceAbility {
  state: "ready" | "installing" | "needs_license" | "needs_virtualization" | "missing" | "failed" | "unsupported";
  detail?: string | null;
  version?: string | null;
  gpu?: boolean | null;
  emulators?: number | null;
}

function query(params: Record<string, string | undefined>): string {
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) out.set(k, v);
  const text = out.toString();
  return text ? `?${text}` : "";
}

const BASE = "/api/v1/departments/accounts";

export const Accounts = {
  list: (params: { agent_id?: string; app?: AccountApp; state?: AccountState } = {}) =>
    api<AccountOut[]>(`${BASE}${query(params)}`),
  get: (id: string) => api<AccountDetail>(`${BASE}/${id}`),
  create: (body: AccountCreateIn) => api<AccountOut>(BASE, { method: "POST", body, idempotent: true }),
  /** Send the version you read: 409 means someone changed it meanwhile. */
  update: (id: string, body: AccountUpdateIn) => api<AccountOut>(`${BASE}/${id}`, { method: "PATCH", body }),
  password: (id: string, version: number, password: string) =>
    api<AccountOut>(`${BASE}/${id}/password`, { method: "POST", body: { version, password } }),
  reveal: (id: string) => api<{ password: string }>(`${BASE}/${id}/reveal`, { method: "POST" }),
  remove: (id: string) => api<void>(`${BASE}/${id}`, { method: "DELETE" }),
};
